import { PROMO, usePromo, formatPromoDate } from '../promo.config';

/**
 * Festive offer card for the home hero. Shows a "starts soon" teaser before
 * the offer, a live countdown while it runs, and disappears once it ends —
 * all driven by the dates in src/promo.config.js.
 */

const pad = n => String(n).padStart(2, '0');

function splitTime(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), mins: Math.floor((s % 3600) / 60), secs: s % 60 };
}

// Twinkling stars scattered over the card
const SPARKS = [
  { top:'16%', left:'34%', size:11, delay:'0s'   },
  { top:'68%', left:'29%', size:7,  delay:'1.2s' },
  { top:'24%', left:'57%', size:8,  delay:'0.6s' },
  { top:'78%', left:'54%', size:6,  delay:'1.8s' },
  { top:'12%', left:'90%', size:9,  delay:'0.9s' },
];

function Diya() {
  return (
    <svg className="offer-diya" viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <radialGradient id="offerDiyaGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFC76B" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#FFC76B" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="offerDiyaFlame" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%"   stopColor="#FF6A00"/>
          <stop offset="55%"  stopColor="#FFC23A"/>
          <stop offset="100%" stopColor="#FFF4CF"/>
        </linearGradient>
        <linearGradient id="offerDiyaBowl" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#E8C97A"/>
          <stop offset="100%" stopColor="#8B6D38"/>
        </linearGradient>
      </defs>
      <circle className="offer-diya-glow" cx="32" cy="24" r="24" fill="url(#offerDiyaGlow)"/>
      <path className="offer-diya-flame" d="M32 8c5 8 7 13 7 17a7 7 0 0 1-14 0c0-4 2-9 7-17z" fill="url(#offerDiyaFlame)"/>
      <path d="M8 34h48c-2 12-12 20-24 20S10 46 8 34z" fill="url(#offerDiyaBowl)"/>
      <path d="M6 34h52" stroke="#E8C97A" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="22" cy="42" r="1.6" fill="#FFF3D6" opacity="0.75"/>
      <circle cx="32" cy="45" r="1.6" fill="#FFF3D6" opacity="0.75"/>
      <circle cx="42" cy="42" r="1.6" fill="#FFF3D6" opacity="0.75"/>
    </svg>
  );
}

// Rangoli-style mandala watermark behind the countdown
function Mandala() {
  return (
    <svg className="offer-mandala" viewBox="0 0 200 200" aria-hidden="true">
      <g fill="none" stroke="#E8C97A" strokeWidth="1">
        <circle cx="100" cy="100" r="96"/>
        <circle cx="100" cy="100" r="72"/>
        <circle cx="100" cy="100" r="20"/>
        {Array.from({ length:12 }, (_, i) => <ellipse key={`p${i}`} cx="100" cy="54" rx="12" ry="30" transform={`rotate(${i*30} 100 100)`}/>)}
        {Array.from({ length:24 }, (_, i) => <circle key={`d${i}`} cx="100" cy="10" r="3" transform={`rotate(${i*15} 100 100)`}/>)}
      </g>
    </svg>
  );
}

const STYLES = `
  .offer { position:relative; display:grid; grid-template-columns:1fr auto; align-items:center; gap:28px; max-width:730px; margin:0 auto 36px; padding:30px 30px 24px; text-align:left; overflow:hidden; color:#FFF6E6;
    background:
      radial-gradient(90% 130% at 6% 55%, rgba(255,176,59,0.30) 0%, rgba(255,176,59,0) 48%),
      radial-gradient(80% 120% at 100% 0%, rgba(201,168,76,0.16) 0%, rgba(201,168,76,0) 55%),
      linear-gradient(135deg, #2B2016 0%, #1C1510 55%, #2A1D10 100%);
    border:1px solid rgba(232,201,122,0.5);
    box-shadow:0 26px 60px rgba(60,40,15,0.32), inset 0 0 0 6px #1E1711, inset 0 0 0 7px rgba(232,201,122,0.2);
    animation:fadeUp 1s 0.7s both; }
  .offer-toran { position:absolute; top:0; left:0; right:0; height:10px; pointer-events:none;
    background:
      radial-gradient(circle at 50% 3px, #F7B32B 0 3.4px, transparent 4px) 0 0 / 16px 10px repeat-x,
      radial-gradient(circle at 50% 3px, #E8651C 0 3.4px, transparent 4px) 8px 0 / 16px 10px repeat-x; }
  .offer-spark { position:absolute; background:#FFE7A3; pointer-events:none; opacity:0;
    clip-path:polygon(50% 0,61% 39%,100% 50%,61% 61%,50% 100%,39% 61%,0 50%,39% 39%);
    animation:offerTwinkle 3.2s ease-in-out infinite; }
  .offer-mandala { position:absolute; right:-70px; top:50%; width:250px; height:250px; margin-top:-125px; opacity:0.09; pointer-events:none; animation:spin 90s linear infinite; }
  .offer-main { position:relative; display:flex; align-items:center; gap:18px; }
  .offer-diya { width:68px; height:68px; flex-shrink:0; overflow:visible; }
  .offer-diya-flame { transform-box:view-box; transform-origin:32px 31px; animation:offerFlicker 1.4s ease-in-out infinite alternate; }
  .offer-diya-glow { transform-box:fill-box; transform-origin:center; animation:offerGlow 2.2s ease-in-out infinite alternate; }
  .offer-kicker { display:flex; align-items:center; flex-wrap:wrap; gap:8px; font-family:'Outfit',sans-serif; font-size:0.58rem; letter-spacing:0.28em; text-transform:uppercase; color:#E8C97A; font-weight:600; }
  .offer-status { display:inline-flex; align-items:center; gap:7px; padding:3px 9px; border:1px solid rgba(232,201,122,0.4); color:#FFF3D6; letter-spacing:0.2em; }
  .offer-dot { width:6px; height:6px; border-radius:50%; background:#FF9F43; animation:offerPulse 1.8s ease-out infinite; }
  .offer-headline { display:flex; align-items:baseline; gap:12px; margin:6px 0 2px; line-height:1; }
  .offer-pct { font-family:'Cormorant Garamond',serif; font-size:clamp(3.4rem,7vw,4.8rem); font-weight:700; letter-spacing:-0.02em; font-variant-numeric:lining-nums;
    background:linear-gradient(100deg,#B8892E 0%,#F6E3A1 22%,#D9B45A 42%,#FFF4C8 55%,#C9A84C 75%,#B8892E 100%); background-size:200% auto;
    -webkit-background-clip:text; background-clip:text; color:transparent; animation:shimmer 6s linear infinite; }
  .offer-off { font-family:'Outfit',sans-serif; font-size:1.1rem; letter-spacing:0.34em; font-weight:700; color:#FFF6E6; }
  .offer-sub { font-family:'DM Sans',sans-serif; font-size:0.95rem; font-weight:300; color:rgba(255,246,230,0.8); }
  .offer-side { position:relative; text-align:center; padding-left:28px; border-left:1px solid rgba(232,201,122,0.22); }
  .offer-count-label { font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.3em; text-transform:uppercase; color:rgba(232,201,122,0.9); font-weight:600; margin-bottom:10px; }
  .offer-count { display:flex; gap:8px; justify-content:center; }
  .offer-unit { width:58px; padding:9px 0 7px; display:flex; flex-direction:column; align-items:center; background:rgba(255,246,230,0.06); border:1px solid rgba(232,201,122,0.28); }
  .offer-num { font-family:'Cormorant Garamond',serif; font-size:1.8rem; font-weight:600; line-height:1; color:#FFF3D6; font-variant-numeric:lining-nums tabular-nums; }
  .offer-lbl { font-family:'Outfit',sans-serif; font-size:0.46rem; letter-spacing:0.22em; text-transform:uppercase; color:rgba(232,201,122,0.85); margin-top:5px; }
  .offer-dates { margin-top:12px; font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.18em; text-transform:uppercase; color:rgba(255,246,230,0.62); }
  .offer-dates b { color:#E8C97A; font-weight:700; letter-spacing:0.22em; }
  @keyframes offerFlicker { 0% { transform:scale(1,1) rotate(-2deg); } 50% { transform:scale(0.94,1.07) rotate(1.5deg); } 100% { transform:scale(1.04,0.95) rotate(-1deg); } }
  @keyframes offerGlow    { from { opacity:0.65; transform:scale(0.92); } to { opacity:1; transform:scale(1.06); } }
  @keyframes offerTwinkle { 0%,100% { opacity:0; transform:scale(0.3) rotate(0deg); } 50% { opacity:1; transform:scale(1) rotate(45deg); } }
  @keyframes offerPulse   { 0% { box-shadow:0 0 0 0 rgba(255,159,67,0.7); } 100% { box-shadow:0 0 0 8px rgba(255,159,67,0); } }
  @media(max-width:640px){
    .offer { grid-template-columns:1fr; gap:18px; padding:28px 18px 20px; text-align:center; }
    .offer-main { flex-direction:column; gap:4px; }
    .offer-diya { width:56px; height:56px; }
    .offer-kicker, .offer-headline { justify-content:center; }
    .offer-side { padding:16px 0 0; border-left:none; border-top:1px solid rgba(232,201,122,0.22); }
    .offer-unit { width:auto; flex:1 1 0; }
    .offer-mandala { right:50%; margin-right:-125px; top:auto; bottom:-150px; margin-top:0; }
  }
  @media(prefers-reduced-motion:reduce){ .offer, .offer * { animation:none !important; } .offer-spark { display:none; } }
`;

export default function HeroOffer() {
  const { phase, now } = usePromo(1000);
  if (phase !== 'live' && phase !== 'upcoming') return null;

  const live  = phase === 'live';
  const t     = splitTime((live ? PROMO.endsAt : PROMO.startsAt) - now);
  const units = [['Days', t.days], ['Hrs', t.hours], ['Min', t.mins], ['Sec', t.secs]];

  return (
    <aside className="offer" aria-label={`${PROMO.name}: ${PROMO.percent}% off ${PROMO.appliesTo}`}>
      <style>{STYLES}</style>
      <span className="offer-toran" aria-hidden="true"/>
      <Mandala/>
      {SPARKS.map((s, i) => (
        <span key={i} className="offer-spark" aria-hidden="true" style={{ top:s.top, left:s.left, width:s.size, height:s.size, animationDelay:s.delay }}/>
      ))}

      <div className="offer-main">
        <Diya/>
        <div>
          <div className="offer-kicker">
            <span className="offer-status">
              {live ? <><span className="offer-dot"/>Live now</> : <>Starts {formatPromoDate(PROMO.startsAt)}</>}
            </span>
            {PROMO.name}
          </div>
          <div className="offer-headline">
            <span className="offer-pct">{PROMO.percent}%</span>
            <span className="offer-off">OFF</span>
          </div>
          <div className="offer-sub">on {PROMO.appliesTo}</div>
        </div>
      </div>

      <div className="offer-side">
        <div className="offer-count-label">{live ? 'Offer ends in' : 'Offer starts in'}</div>
        <div className="offer-count" role="timer" aria-label={`${live ? 'Ends' : 'Starts'} in ${t.days} days, ${t.hours} hours and ${t.mins} minutes`}>
          {units.map(([label, value]) => (
            <div key={label} className="offer-unit" aria-hidden="true">
              <span className="offer-num">{pad(value)}</span>
              <span className="offer-lbl">{label}</span>
            </div>
          ))}
        </div>
        <div className="offer-dates">
          {formatPromoDate(PROMO.startsAt)} – {formatPromoDate(PROMO.endsAt, true)}
          {PROMO.code && <> · Code <b>{PROMO.code}</b></>}
        </div>
      </div>
    </aside>
  );
}
